import React, { useEffect, useState } from "react";

import { Header, PageWrapper } from "src/shared/components";

import { sumBufferedHours } from "../shared/utils/buffer";

import EditVolunteer from "./components/EditVolunteer";
import VolunteerModal from "./components/VolunteerModal";
import VolunteersTable from "./components/VolunteersTable";
import {
  GetVolunteersQuery,
  useGetVolunteersLazyQuery,
} from "./graphql/getVolunteers.generated";

export interface Shift {
  id: number;
  name: string;
  checkedIn: boolean;
  startTime: Date;
  endTime: Date;
  role: string;
}

export interface Volunteer {
  eventAttendeeId: number;
  name: string;
  email: string;
  primaryRole: string;
  additionalRoles: string[];
  shifts: Shift[];
  minPrimarySignups: number;
  hoursCompleted: number;
}

const parseVolunteers = (data: GetVolunteersQuery): Volunteer[] =>
  data.volunteer.map(
    ({
      primary_category_name,
      min_primary_signups,
      event_attendee: {
        id,
        user: { name, email },
        shift_signup,
      },
    }) => {
      const shifts: Shift[] =
        shift_signup?.map(
          ({
            checked_in,
            shift: { id, name, start_time, end_time, category },
          }) => {
            return {
              id,
              name,
              checkedIn: checked_in,
              startTime: new Date(start_time),
              endTime: new Date(end_time),
              role: category ?? "",
            };
          }
        ) ?? [];

      const primarySignupCount = shifts.filter(
        ({ role }) => role === primary_category_name
      ).length;
      const additionalRoles =
        primarySignupCount >= min_primary_signups
          ? ["Floor Patrol", "Security", "Supplementary"]
          : [];

      const hoursCompleted = sumBufferedHours(
        shifts.map(({ id, startTime, endTime, checkedIn }) => ({
          shiftId: id,
          start: startTime,
          end: endTime,
          checkedIn,
        })),
        { checkedInOnly: true, pastOnly: true }
      );

      return {
        eventAttendeeId: id,
        name,
        email,
        primaryRole: primary_category_name,
        additionalRoles,
        shifts,
        minPrimarySignups: min_primary_signups,
        hoursCompleted,
      };
    }
  );

const VolunteersPage = () => {
  const [viewVolunteer, setViewVolunteer] = useState<null | Volunteer>(null);
  const [editVolunteer, setEditVolunteer] = useState<null | Volunteer>(null);

  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [getVolunteers, { data, refetch, loading }] =
    useGetVolunteersLazyQuery();

  useEffect(() => {
    getVolunteers();
  }, [getVolunteers]);

  useEffect(() => {
    if (data && !loading) {
      const updatedVolunteers = parseVolunteers(data);
      setVolunteers(updatedVolunteers);

      if (viewVolunteer) {
        const updatedVolunteer = updatedVolunteers.find(
          (v) => v.eventAttendeeId === viewVolunteer.eventAttendeeId
        );
        if (
          updatedVolunteer &&
          updatedVolunteer.shifts.length !== viewVolunteer.shifts.length
        ) {
          setViewVolunteer(updatedVolunteer);
        }
      }
    }
  }, [data, loading, viewVolunteer]);

  return (
    <>
      <Header
        title="Volunteers"
        subtitle="View our database of 300+ volunteers"
        // actionButton={
        //   <Button
        //     leadingIcon={
        //       <Icon name="spon-download" size="14px" color="transparent" />
        //     }
        //     color="primary"
        //     onClick={() => window.open(VOLUNTEER_CSV_DOWNLOAD_LINK, "_blank")}
        //   >
        //     Download CSVs
        //   </Button>
        // }
      />
      <PageWrapper>
        {editVolunteer ? (
          <EditVolunteer volunteer={editVolunteer} />
        ) : (
          <>
            <VolunteersTable
              setViewVolunteer={setViewVolunteer}
              volunteers={volunteers}
              loading={loading}
            />
            {viewVolunteer && refetch && (
              <VolunteerModal
                volunteer={viewVolunteer}
                refetch={refetch}
                open={viewVolunteer !== null}
                onClose={() => setViewVolunteer(null)}
                onEdit={() => {
                  setEditVolunteer(viewVolunteer);
                  setViewVolunteer(null);
                }}
              />
            )}
          </>
        )}
      </PageWrapper>
    </>
  );
};

export default VolunteersPage;
